import React, { ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import styled from '@emotion/styled'
import { useNavigate, useLocation, Location } from 'react-router'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font } from '@mobi/component-library/Foundations'
import { greaterThan, lessOrEqualThan } from '@mobi/component-library/Utils/media'
import { AppRoutes } from '@core/AppRoutes'
import { MainContainer } from '../Containers'

const smallScreen = lessOrEqualThan('px375')
const largeScreen = greaterThan('px375')

interface ClassProps {
  location: Location
  onReset: () => void
  children?: ReactNode
}

interface ClassState {
  hasError: boolean
}

// Wrapper for react class to use hooks
export const ContentErrorBoundary = ({ children }: { children?: ReactNode }): React.ReactNode => {
  const navigate = useNavigate()
  const location = useLocation()

  // Navigate home, if already there try refreshing the page
  const onReset = () => {
    if (location.pathname !== AppRoutes.Home) navigate(AppRoutes.Home)
    window.location.reload()
  }

  return (
    <ContentErrorBoundaryClass onReset={onReset} location={location}>
      {children}
    </ContentErrorBoundaryClass>
  )
}

class ContentErrorBoundaryClass extends React.Component<ClassProps, ClassState> {
  public state: ClassState = {
    hasError: false,
  }

  public static getDerivedStateFromError(): ClassState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureReactException(error, errorInfo)
  }

  // Clear error on route change
  componentDidUpdate(prevProps: ClassProps) {
    const { location } = this.props
    if (location.pathname !== prevProps.location.pathname) {
      this.setState({ hasError: false })
    }
  }

  public render() {
    const { children, onReset } = this.props
    const { hasError } = this.state

    if (hasError) {
      return (
        <MainContainer background='white'>
          <ContentErrorBoundaryStyled>
            <header>Something went wrong</header>

            <div className='description'>
              If you were in the process of placing a bet or making a deposit, check your account
              transactions or call customer support on <b>1300 36 36 88</b>
            </div>

            <Button color='primary' size={largeScreen ? 'sm' : 'xs'} onClick={onReset}>
              Reload
            </Button>
          </ContentErrorBoundaryStyled>
        </MainContainer>
      )
    }

    return children
  }
}

const ContentErrorBoundaryStyled = styled.div({
  margin: 'auto',
  maxWidth: '65%',
  fontFamily: font.family.primary,
  display: 'grid',
  gap: '3rem',

  header: {
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: font.weight.semibold,
    [smallScreen]: {
      ...font.size.xl,
    },
    [largeScreen]: {
      ...font.size.xl6,
    },
    'div.description': {
      display: 'flex',
      flexDirection: 'row',
      color: colors.neutral[800],
      fontWeight: font.weight.regular,
      [smallScreen]: {
        ...font.size.md,
      },
      [largeScreen]: {
        padding: '0 2rem',
        ...font.size.lg,
      },
    },
  },
})
